import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowLeft, Award } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "@/components/Motion";
import BookingDialog from "@/components/BookingDialog";
import Seo from "@/components/Seo";
import { COURSES, IMAGES } from "@/data/content";

export default function CorsoDetail() {
  const { slug } = useParams();
  const c = COURSES.find((x) => x.slug === slug);
  if (!c) return <Navigate to="/academy" replace />;

  return (
    <div className="pt-24 pb-28 min-h-screen">
      <Seo title={`${c.title} · Lux Academy`} description={`${c.subtitle || ""} ${c.description || ""}`} image={IMAGES.academy}
        jsonLd={{ "@context": "https://schema.org", "@type": "Course", name: c.title, description: c.description, inLanguage: "it",
          provider: { "@type": "Organization", name: "LUX AETERNA — Lux Academy" },
          hasCourseInstance: { "@type": "CourseInstance", courseMode: ["online", "onsite"] } }} />
      <div className="relative h-[45vh] min-h-[340px] overflow-hidden constellation">
        <img src={IMAGES.academy} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#F7EFE0] via-[#F7EFE0]/60 to-transparent" />
        <div className="absolute inset-0 max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-12">
          <Link to="/academy" className="inline-flex items-center gap-2 text-[#2A1F14]/50 hover:text-gold text-sm mb-6 w-fit"><ArrowLeft size={16} /> Lux Academy</Link>
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">{c.level} · {c.duration}</p>
          <h1 className="font-display text-4xl sm:text-6xl text-[#2A1F14] leading-tight">{c.title}</h1>
          <p className="font-display italic text-xl text-[#2A1F14]/60 mt-3">{c.tagline}</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-16 mt-16">
        <div className="lg:col-span-2 space-y-14">
          <Reveal>
            <h2 className="font-display text-2xl text-gold mb-5">Il percorso</h2>
            <p className="text-[#2A1F14]/65 text-lg leading-relaxed">{c.description}</p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-gold mb-6">Programma e moduli</h2>
            <div className="space-y-px">
              {c.modules.map((m, i) => (
                <div key={i} className="flex items-center gap-5 border-t border-[#2A1F14]/10 py-4">
                  <span className="font-display text-gold/50 text-lg w-8">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[#2A1F14]/75">{m}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-gold mb-6">Cosa include</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {c.includes.map((x) => (
                <div key={x} className="flex items-start gap-3 border border-[#2A1F14]/10 rounded-xl p-5">
                  <Check size={18} className="text-gold mt-0.5 shrink-0" />
                  <span className="text-[#2A1F14]/75 text-sm">{x}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-gold mb-6">Domande frequenti</h2>
            <Accordion type="single" collapsible className="border-t border-[#2A1F14]/10">
              {c.faq.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} className="border-[#2A1F14]/10">
                  <AccordionTrigger className="text-[#2A1F14] hover:text-gold text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[#2A1F14]/55 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>

        <div className="lg:col-span-1">
          <div className="glass rounded-2xl p-8 sticky top-24">
            <div className="flex items-center gap-2 text-gold mb-4"><Award size={18} /><span className="text-xs uppercase tracking-[0.2em]">Attestato incluso</span></div>
            <h3 className="font-display text-2xl text-[#2A1F14] mb-3">Iscriviti</h3>
            <div className="flex gap-2 mb-6">
              {c.modes.map((m) => <span key={m} className="text-[10px] uppercase tracking-wide text-[#2A1F14]/50 border border-[#2A1F14]/15 rounded-full px-3 py-1">{m}</span>)}
            </div>
            <p className="text-[#2A1F14]/50 text-sm mb-8 leading-relaxed">Scegli la modalità e invia la tua richiesta di iscrizione. Riceverai il programma completo e i dettagli via email.</p>
            <BookingDialog title={`Iscrizione — ${c.title}`} endpoint="enrollments"
              presets={{ course: c.title, level: c.level, mode: c.modes[0] }}
              trigger={<button data-testid="course-enroll" className="w-full py-4 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors">Iscriviti al corso</button>} />
          </div>
        </div>
      </div>
    </div>
  );
}
